<template>
    <section :id="id" class="s-data-centers-local" :class="{ ...basicClassName, ...className }">
        <div class="a-container">
            <div class="content">
                <a-slice-header :title="l10n.title" :lead="l10n.lead" />
                <a-picture
                    :link="mapImageLink"
                    class="data-centers-image"
                    fit="cover"
                    :style="{ height: `${mapContainerHeight}px` }"
                />
                <div class="certificate">
                    <div class="certificate-inner">
                        <div class="label-wrap">
                            <a-label
                                v-if="l10n.certificateLabel"
                                size="s"
                                variant="neutral"
                                layout="subtle"
                                v-bind="l10n.certificateLabel"
                            >
                                {{ l10n.certificateLabel.text }}
                            </a-label>
                        </div>
                        <h4 class="certificate-title">
                            {{ l10n.certificateTitle }}
                        </h4>
                        <div class="certificate-text">
                            {{ l10n.certificateText }}
                        </div>
                        <div class="logos">
                            <a-picture
                                v-for="logo in l10n.certificateLogos"
                                :key="logo"
                                :link="logo"
                                class="logo"
                            />
                        </div>
                    </div>
                </div>
                <div class="address">
                    <h4 v-if="l10n.addressTitle" class="address-title">
                        {{ l10n.addressTitle }}
                    </h4>
                    <div v-if="l10n.addressText" class="address-text">
                        <a-glyph name="pin--16" />
                        {{ l10n.addressText }}
                    </div>
                    <a-link
                        v-if="l10n.phone"
                        glyph="phone2"
                        glyph-position="left"
                        class="phone"
                        :to="`tel:${l10n.phone}`"
                        size="paragraph"
                        :text="l10n.phone"
                        :type="theme === 'dark' ? 'light' : 'regular'"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import AGlyph from '@core/components/glyph/glyph.vue';
import ALabel from '@core/components/label/label.vue';
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import ASliceHeader from '@core/components/slice-header/slice-header.vue';
import breakpoint from '@core/mixins/breakpoint.js';
import contentMixin from '@core/mixins/content.js';
import l10n from '@core/mixins/l10n.js';
import styleMixin from '@core/mixins/style.js';

export default {
    name: 'SDataCentersLocal',
    components: {
        ASliceHeader,
        AGlyph,
        ALink,
        ALabel,
        APicture,
    },
    mixins: [contentMixin, styleMixin, breakpoint, l10n],

    props: {
        /**
         * Default data centers map image (for desktop)
         */
        mapImage: {
            type: String,
            required: true,
        },
        /**
         * Centered data centers map image (for mobile and tablet)
         */
        mapImageCentered: {
            type: String,
            required: true,
        },
        /**
         * Height of the data centers map container
         */
        mapContainerHeight: {
            type: Number,
            default: 300,
        },

        addressTitle: {
            type: String,
            default: null,
        },

        addressText: {
            type: String,
            default: null,
        },

        phone: {
            type: String,
            default: null,
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'light',
        },

        certificateLabel: {
            type: Object,
            default: null,
        },

        certificateTitle: {
            type: String,
            default: null,
        },

        certificateText: {
            type: String,
            default: null,
        },

        certificateLogos: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        className() {
            return {
                'dark': this.theme === 'dark',
            };
        },
        mapImageLink() {
            return this.isDesktop ? this.mapImage : this.mapImageCentered;
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-data-centers-local {
    @mixin basic-slice-paddings;

    &.dark {
        background: var(--av-nav-primary);
        color: var(--av-inversed-primary);

        &:deep(.a-slice-header__title),
        &:deep(.a-slice-header__lead),
        &:deep(.a-slice-header__body-text),
        .address-title {
            color: var(--av-inversed-primary);
        }

        .address-text {
            .a-glyph {
                fill: var(--av-inversed-primary);
            }
        }

        .a-slice-header:deep(a) {
            color: var(--av-link-inversed);
            &:hover {
                color: var(--av-link-inversed-hover);
            }
        }

        .phone:deep(.a-glyph) {
            fill: var(--av-inversed-primary);
        }

        .data-centers-image {
            border-color: var(--av-brand-light);
        }
    }
}

.a-slice-header {
    margin-bottom: 24px;

    @media (--viewport-mobile-wide) {
        margin-bottom: 32px;
    }

    @media (--viewport-desktop) {
        @mixin colls 6;
    }

    &__title {
        margin-bottom: 16px;

        @media (--viewport-mobile-wide) {
            margin-bottom: 32px;
        }
    }
}

.content {
    position: relative;
}

.data-centers-image {
    border: 1px solid var(--av-brand-accent);
    border-radius: 8px;
    overflow: hidden;

    &:deep(.a-picture__img) {
        width: 100%;
        height: 100%;
    }
}

.certificate {
    position: relative;
    margin-top: -16px;

    .a-label {
        margin-bottom: 8px;
    }

    @media (--viewport-mobile-wide) {
        margin: -32px 24px 0;
    }

    @media (--viewport-desktop) {
        @mixin colls 5;
        position: absolute;
        inset-inline-end: 0;
        top: 0;
        margin: 0;
        padding-inline-end: 24px;
    }
}

.certificate-inner {
    padding: 24px;
    border-radius: 8px;
    border: 1px solid var(--av-brand-secondary-light);
    background: var(--av-inversed-primary);
    box-shadow: var(--av-shadow-small);
}

.label-wrap {
    display: flex;
}

.certificate-title {
    @mixin title-accent;
    color: var(--av-nav-primary);
    margin-bottom: 8px;
}

.certificate-text {
    @mixin paragraph;
    color: var(--av-fixed-secondary);
    margin-bottom: 40px;
}

.logos {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex-wrap: wrap;
    row-gap: 24px;
    column-gap: 32px;

    @media (--viewport-mobile-wide) {
        flex-direction: row;
    }
}

.logo {
    height: 32px;
}

.address {
    margin-top: 24px;
    @media (--viewport-mobile-wide) {
        margin-top: 32px;
    }
}

.address-title {
    @mixin title-accent;
    color: var(--av-nav-primary);
    margin-bottom: 8px;
}

.address-text {
    margin-bottom: 8px;
    display: flex;
    gap: 8px;

    .a-glyph {
        fill: var(--av-brand-primary);
        margin-top: 4px;
        flex-shrink: 0;
    }
}

.phone {
    @mixin paragraph-accent;
}
</style>
